import { useMutation } from '@apollo/client';
import React from 'react';

import { LanguageSelectorComboBox } from '@eluve/blocks';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  Separator,
  useToast,
} from '@eluve/components';
import { DEFAULT_LANGUAGE } from '@eluve/language-utils';
import { useAnalytics } from '@eluve/posthog-react';
import {
  useTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';

import {
  updateInputLanguageMutation,
  updateOutputLanguageMutation,
} from './operations';
import { useTenantUserSettings } from './useTenantUserSettings';

export const LanguageSettings: React.FC = () => {
  const toast = useToast();
  const userId = useUserIdFromSession();
  const tenantId = useTenantIdFromParams()!;
  const { settings } = useTenantUserSettings();

  const analytics = useAnalytics('user_settings');

  const inputLanguageCode = settings?.inputLanguage ?? DEFAULT_LANGUAGE;
  const outputLanguageCode = settings?.outputLanguage ?? DEFAULT_LANGUAGE;

  const [updateInputLanguage] = useMutation(updateInputLanguageMutation, {
    onError: () => toast.error('Failed to update language'),
  });
  const [updateOutputLanguage] = useMutation(updateOutputLanguageMutation, {
    onError: () => toast.error('Failed to update language'),
  });

  const handleInputLanguageSelected = async (languageCode: string) => {
    await updateInputLanguage({
      variables: {
        userId,
        tenantId,
        inputLanguage: languageCode,
      },
      optimisticResponse: () => ({
        updateTenantUserSettingsByPk: {
          __typename: 'TenantUserSettings',
          userId,
          tenantId,
          inputLanguage: languageCode,
        },
      }),
      onCompleted: (data) => {
        if (data.updateTenantUserSettingsByPk) {
          const { tenantId, userId, inputLanguage } =
            data.updateTenantUserSettingsByPk;
          analytics.capture('input_language_update', {
            tenantId,
            userId,
            inputLanguage,
          });
        }
      },
    });
  };
  const handleOutputLanguageSelected = async (languageCode: string) => {
    await updateOutputLanguage({
      variables: {
        userId,
        tenantId,
        outputLanguage: languageCode,
      },
      optimisticResponse: () => ({
        updateTenantUserSettingsByPk: {
          __typename: 'TenantUserSettings',
          userId,
          tenantId,
          outputLanguage: languageCode,
        },
      }),
      onCompleted: (data) => {
        if (data.updateTenantUserSettingsByPk) {
          const { tenantId, userId, outputLanguage } =
            data.updateTenantUserSettingsByPk;
          analytics.capture('output_language_update', {
            tenantId,
            userId,
            outputLanguage,
          });
        }
      },
    });
  };

  const preferredLanguages = [];
  if (settings?.inputLanguage) {
    preferredLanguages.push(settings.inputLanguage);
  }
  if (settings?.outputLanguage) {
    preferredLanguages.push(settings.outputLanguage);
  }

  return (
    <>
      <Field orientation="horizontal" className="px-6 py-5">
        <FieldContent>
          <FieldLabel>Notes Language</FieldLabel>
          <FieldDescription>
            Language used in the generated note.
          </FieldDescription>
        </FieldContent>
        <div className="flex w-full max-w-60 justify-end">
          <LanguageSelectorComboBox
            selectButtonStyles="w-full"
            variant="solid"
            showLanguageIcon={false}
            selectedLanguageCode={outputLanguageCode}
            onSelectedLanguageCode={handleOutputLanguageSelected}
            preferredLanguages={preferredLanguages}
            ariaLabelPrefix="Notes Language"
          />
        </div>
      </Field>
      <Separator className="my-0" />
      <Field orientation="horizontal" className="px-6 py-5">
        <FieldContent>
          <FieldLabel>Spoken Language</FieldLabel>
          <FieldDescription>
            Language spoken during your sessions.
          </FieldDescription>
        </FieldContent>
        <div className="flex w-full max-w-60 justify-end">
          <LanguageSelectorComboBox
            selectButtonStyles="w-full"
            variant="solid"
            showLanguageIcon={false}
            selectedLanguageCode={inputLanguageCode}
            onSelectedLanguageCode={handleInputLanguageSelected}
            preferredLanguages={preferredLanguages}
            ariaLabelPrefix="Spoken Language"
          />
        </div>
      </Field>
    </>
  );
};
